import { Link } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { ArrowUpRight } from "lucide-react";

const stockState = (product) => {
  const stock = Number(product.stock ?? 0);
  if (stock > 0) return "in_stock";
  if (product.pre_order) return "pre_order";
  return "out_of_stock";
};

export default function ProductCard({ product }) {
  const { add } = useCart();
  const state = stockState(product);
  const disabled = state === "out_of_stock";

  return (
    <div
      className="group flex flex-col bg-transparent"
      data-testid={`product-card-${product.slug}`}
    >
      <Link
        to={`/product/${product.slug}`}
        className="block overflow-hidden bg-muted relative"
      >
        <img
          src={product.image}
          alt={product.name}
          className={`w-full aspect-square object-cover transition-transform duration-700 ease-out group-hover:scale-105 ${
            disabled ? "opacity-60" : ""
          }`}
        />
        {(state === "pre_order" || state === "out_of_stock") && (
          <div
            className="absolute top-0 right-0 w-[120px] h-[120px] overflow-hidden pointer-events-none"
            data-testid={`ribbon-${state}-${product.slug}`}
            aria-hidden="true"
          >
            <div
              className={`absolute top-[22px] right-[-40px] w-[170px] text-center py-1.5 text-[10px] uppercase tracking-[0.22em] font-semibold rotate-45 shadow-sm ${
                state === "pre_order"
                  ? "bg-primary text-primary-foreground"
                  : "bg-secondary text-secondary-foreground"
              }`}
            >
              {state === "pre_order" ? "Pre-order" : "Sold out"}
            </div>
          </div>
        )}
      </Link>
      <div className="pt-5 flex flex-col gap-2">
        <p className="overline text-muted-foreground">
          {product.categories?.[0]?.replace("-", " ") || ""}
        </p>
        <div className="flex items-baseline justify-between gap-4">
          <Link
            to={`/product/${product.slug}`}
            className="editorial-heading text-2xl text-secondary hover:text-primary transition-colors"
          >
            {product.name}
          </Link>
          <p className="text-sm font-medium text-secondary whitespace-nowrap">
            ${product.price.toFixed(2)}
          </p>
        </div>
        <p
          data-testid={`bac-water-tag-${product.slug}`}
          className="inline-flex items-center gap-1.5 self-start text-[10px] uppercase tracking-[0.18em] text-primary font-semibold bg-primary/10 px-2 py-1"
        >
          + Bac water included
        </p>
        <p className="text-sm text-muted-foreground leading-relaxed">
          {product.short_description}
        </p>
        <div className="flex items-center gap-4 pt-3">
          {state === "in_stock" && (
            <button
              onClick={() => add(product, 1)}
              data-testid={`add-to-cart-${product.slug}`}
              className="text-[11px] uppercase tracking-[0.22em] font-semibold text-secondary border-b border-secondary pb-1 hover:text-primary hover:border-primary transition-colors"
            >
              Add to cart
            </button>
          )}
          {state === "pre_order" && (
            <button
              onClick={() => add(product, 1)}
              data-testid={`preorder-${product.slug}`}
              className="text-[11px] uppercase tracking-[0.22em] font-semibold text-primary border-b border-primary pb-1 hover:opacity-70 transition-opacity"
            >
              Pre-order
            </button>
          )}
          {state === "out_of_stock" && (
            <span
              data-testid={`disabled-${product.slug}`}
              className="text-[11px] uppercase tracking-[0.22em] font-semibold text-muted-foreground border-b border-muted-foreground/30 pb-1 cursor-not-allowed"
            >
              Out of stock
            </span>
          )}
          <Link
            to={`/product/${product.slug}`}
            className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground hover:text-secondary flex items-center gap-1"
            data-testid={`view-product-${product.slug}`}
          >
            Details <ArrowUpRight className="h-3 w-3" />
          </Link>
        </div>
      </div>
    </div>
  );
}
